import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Users, Search, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Field, EmptyState } from "@/components/common";
import { CustomerDetails } from "@/components/CustomerDetails";
import { Birthdays } from "@/components/Birthdays";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDate, maskPhone, maskDoc, canDo } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", document: "", phone: "", whatsapp: "", email: "", address: "", birth_date: "", notes: "", active: true };

function CustomerForm({ edit, setEdit, onSaved }) {
  const set = (k, mask) => (e) => setEdit((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  const save = async () => {
    const { id, tenant_id, created_at, total_spent, purchases_count, last_purchase_at, loyalty_balance, ...body } = edit;
    try {
      id ? await api.put(`/customers/${id}`, body) : await api.post("/customers", body);
      toast.success("Cliente salvo");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-modal">
        <DialogHeader><DialogTitle>{edit?.id ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
        {edit && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome" className="col-span-2"><Input value={edit.name} onChange={set("name")} data-testid="customer-name-input" /></Field>
            <Field label="CPF ou CNPJ"><Input value={edit.document || ""} onChange={set("document", maskDoc)} data-testid="customer-document-input" /></Field>
            <Field label="Data de nascimento"><Input type="date" value={edit.birth_date || ""} onChange={set("birth_date")} data-testid="customer-birth-input" /></Field>
            <Field label="Telefone"><Input value={edit.phone || ""} onChange={set("phone", maskPhone)} data-testid="customer-phone-input" /></Field>
            <Field label="WhatsApp"><Input value={edit.whatsapp || ""} onChange={set("whatsapp", maskPhone)} data-testid="customer-whatsapp-input" /></Field>
            <Field label="E-mail" className="col-span-2"><Input value={edit.email || ""} onChange={set("email")} data-testid="customer-email-input" /></Field>
            <Field label="Endereço" className="col-span-2"><Input value={edit.address || ""} onChange={set("address")} data-testid="customer-address-input" /></Field>
            <Field label="Observações" className="col-span-2"><Textarea rows={2} value={edit.notes || ""} onChange={set("notes")} data-testid="customer-notes-input" /></Field>
            <label className="col-span-2 flex items-center justify-between rounded-lg border px-3 py-2 text-sm">Cliente ativo<Switch checked={edit.active !== false} onCheckedChange={(v) => setEdit((x) => ({ ...x, active: v }))} data-testid="customer-active-switch" /></label>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={!edit?.name} data-testid="save-customer-btn">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const { session } = useAuth();
  const canDelete = canDo(session.user, "delete_records");
  const loyPoints = session.tenant.settings.loyalty_mode === "points";
  const load = useCallback(() => api.get("/customers").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const filtered = list.filter((c) => (status === "all" || (status === "active") === (c.active !== false)) && `${c.name} ${c.phone} ${c.whatsapp} ${c.email} ${c.document}`.toLowerCase().includes(q.toLowerCase()));
  const remove = async (c) => {
    if (!window.confirm(`Excluir ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader eyebrow="Relacionamento" title="Clientes" subtitle={`${list.length} clientes · ${list.filter((c) => c.active !== false).length} ativos`} actions={<Button onClick={() => setEdit(EMPTY)} data-testid="new-customer-btn"><Plus className="h-4 w-4 mr-1" />Novo cliente</Button>} />
      <div className="flex flex-wrap gap-2">
        <div className="relative w-full max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, telefone, CPF/CNPJ" className="pl-9" data-testid="customers-search-input" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border bg-transparent px-3 text-sm" data-testid="customers-status-filter"><option value="all">Todos</option><option value="active">Ativos</option><option value="inactive">Inativos</option></select>
      </div>
      {!list.length ? <EmptyState icon={Users} title="Nenhum cliente" text="Cadastre clientes para vincular às vendas, pedidos e fidelidade." /> : (
        <div className="card-surface overflow-x-auto">
          <Table data-testid="customers-table">
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Contato</TableHead><TableHead className="text-right">Total comprado</TableHead><TableHead>Última compra</TableHead><TableHead className="text-right">Fidelidade</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className={c.active === false ? "opacity-50" : ""}>
                  <TableCell><p className="font-medium">{c.name}</p><p className="font-mono text-xs text-muted-foreground">{c.document || ""}</p></TableCell>
                  <TableCell className="text-sm">{c.whatsapp || c.phone || "—"}<br /><span className="text-xs text-muted-foreground">{c.email}</span></TableCell>
                  <TableCell className="text-right font-mono">{brl(c.total_spent)}</TableCell>
                  <TableCell>{c.last_purchase_at ? fmtDate(c.last_purchase_at) : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{loyPoints ? `${Math.round(c.loyalty_balance || 0)} pts` : brl(c.loyalty_balance)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => setView(c.id)} data-testid={`view-customer-${c.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <CustomerForm edit={edit} setEdit={setEdit} onSaved={() => { setEdit(null); load(); }} />
      <Birthdays store={session.tenant.nome_fantasia} />
      <CustomerDetails id={view} onClose={() => setView(null)} />
    </>
  );
}
